import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { Switch } from '@headlessui/react';
import { PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/20/solid';
import { generatePackingSlipPDF } from '../utils/packingSlipUtils';

function Deliveries() {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUninvoiced, setShowUninvoiced] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  const [editShipDate, setEditShipDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [invoiceEntirePackage, setInvoiceEntirePackage] = useState(false); // New state for checkbox
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [customers, setCustomers] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);


  document.title = 'Deliveries';

  useEffect(() => {
    const fetchDeliveries = async () => {
      if (!user) return;
    
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('deliveries')
          .select(`
            delivery_id, package_number, po_number, part_number, quantity, ship_date, invoice_number, tracking_number, notes, packing_slip_path,
            job_id, kanban_job_id,
            jobs!left(unit_price, customer_id, customers!left(nickname)),
            kanban_jobs!left(unit_price, customers!left(nickname))
          `)
          .order('ship_date', { ascending: false });
        if (error) throw new Error(`Deliveries fetch error: ${error.message}`);
    
        const deliveriesData = Array.isArray(data) ? data : [];
        const updatedDeliveries = deliveriesData.map(delivery => ({
          ...delivery,
          customer_nickname: delivery.jobs?.customers?.nickname || delivery.kanban_jobs?.customers?.nickname || '-',
          unit_price: delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0,
        }));
        setDeliveries(updatedDeliveries);
        setFilteredDeliveries(updatedDeliveries);
    
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, nickname')
          .order('nickname');
        if (customersError) throw new Error(`Customers fetch error: ${customersError.message}`);
        setCustomers(customersData || []);
      } catch (err) {
        console.error('Error fetching deliveries:', err.message);
        toast.error(`Failed to fetch deliveries: ${err.message}`);
        setDeliveries([]);
        setFilteredDeliveries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveries();
  }, [user]);

  const handleFilter = () => {
    let filtered = [...deliveries];
    if (showUninvoiced) {
      filtered = filtered.filter(delivery => !delivery.invoice_number);
    }
    if (filterCustomer) {
      filtered = filtered.filter(delivery =>
        (delivery.job_id && delivery.jobs?.customer_id === parseInt(filterCustomer)) ||
        (delivery.kanban_job_id && delivery.kanban_jobs?.customers?.id === parseInt(filterCustomer))
      );
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(delivery =>
        (delivery.package_number?.toLowerCase() || '').includes(searchLower) ||
        (delivery.po_number?.toLowerCase() || '').includes(searchLower)
      );
    }

    setFilteredDeliveries(filtered);
  };

  

  useEffect(() => {
    handleFilter();
  }, [showUninvoiced, deliveries, filterCustomer, searchTerm]);

  const openEditModal = (delivery) => {
    setSelectedDelivery(delivery);
    setEditInvoiceNumber(delivery.invoice_number || '');
    setEditTrackingNumber(delivery.tracking_number || '');
    setEditShipDate(delivery.ship_date || '');    
    setEditNotes(delivery.notes || '');
    setEditQuantity(delivery.quantity || '');
    setInvoiceEntirePackage(false); // Reset checkbox state
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDelivery(null);
    setEditInvoiceNumber('');
    setEditTrackingNumber('');
    setEditShipDate('');
    setEditNotes('');
    setEditQuantity('');
    setInvoiceEntirePackage(false);
  };

  const openPdfModal = async (packingSlipPath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .createSignedUrl(packingSlipPath, 60);
      if (error) throw new Error(`Failed to generate signed URL for packing slip: ${error.message}`);
  
      // Open the PDF in a new tab
      window.open(data.signedUrl, '_blank');
  
      // Refresh the current tab
      window.location.reload();
    } catch (err) {
      console.error('Error opening packing slip:', err);
      toast.error(`Failed to open packing slip: ${err.message}`);
    }
  };
  

  const refreshDeliveries = async () => {
    try {
      const { data, error } = await supabase
        .from('deliveries')
        .select(`
          delivery_id, package_number, po_number, part_number, quantity, ship_date, invoice_number, tracking_number, notes, packing_slip_path,
          job_id, kanban_job_id,
          jobs!left(unit_price, customer_id, customers!left(nickname)),
          kanban_jobs!left(unit_price, customers!left(nickname))
        `)
        .order('ship_date', { ascending: false });
      if (error) throw new Error(`Deliveries fetch error: ${error.message}`);
  
      const deliveriesData = Array.isArray(data) ? data : [];
      const updatedDeliveries = deliveriesData.map(delivery => ({
        ...delivery,
        customer_nickname: delivery.jobs?.customers?.nickname || delivery.kanban_jobs?.customers?.nickname || '-',
        unit_price: delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0,
      }));
      setDeliveries(updatedDeliveries);
      setFilteredDeliveries(updatedDeliveries);
    } catch (err) {
      console.error('Error refreshing deliveries:', err);
      // Don't show error toast here since this is background refresh
    }
  };

  // Group deliveries by package_number
const groupedDeliveries = filteredDeliveries.reduce((acc, delivery) => {
  const packageNumber = delivery.package_number;
  if (!acc[packageNumber]) {
    acc[packageNumber] = {
      package_number: packageNumber,
      po_number: delivery.po_number,
      customer_nickname: delivery.customer_nickname,
      total_quantity: 0,
      ship_date: delivery.ship_date,
      unit_price: delivery.unit_price,
      invoice_number: delivery.invoice_number,
      tracking_number: delivery.tracking_number,
      packing_slip_path: delivery.packing_slip_path,
      deliveries: [],
      unique_part_numbers: new Set(), // Use a Set to track unique part numbers
    };
  }
  acc[packageNumber].total_quantity += delivery.quantity;
  acc[packageNumber].deliveries.push(delivery);
  acc[packageNumber].unique_part_numbers.add(delivery.part_number); // Add part number to the Set
  return acc;
}, {});

// Add unique_part_count to each package group and convert to array
const groupedDeliveriesArray = Object.values(groupedDeliveries).map(group => ({
  ...group,
  unique_part_count: group.unique_part_numbers.size, // Calculate the count of unique part numbers
  unique_part_numbers: undefined, // Remove the Set from the final object
}));

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setPdfUrl(null);
  };

  const handleUpdateDelivery = async () => {
    // Validate inputs
    if (editQuantity <= 0) {
      toast.error('Quantity must be a positive number.');
      return;
    }
    if (editShipDate && isNaN(new Date(editShipDate).getTime())) {
      toast.error('Please enter a valid ship date.');
      return;
    }

    setIsUpdating(true); // Start loading
  
    try {
      // Update all deliveries with the same package_number if "Invoice Entire Package" is checked
      if (invoiceEntirePackage) {
        const { error } = await supabase
          .from('deliveries')
          .update({
            invoice_number: editInvoiceNumber || null,
            quickbooks_invoice_id: editInvoiceNumber || null,
          })
          .eq('package_number', selectedDelivery.package_number);
        if (error) throw new Error(`Failed to update package deliveries: ${error.message}`);
      }
  
      // Update the current delivery with all fields
      const { error: singleError } = await supabase
        .from('deliveries')
        .update({
          invoice_number: editInvoiceNumber || null,
          quickbooks_invoice_id: editInvoiceNumber || null,
          tracking_number: editTrackingNumber || null,
          ship_date: editShipDate || null,
          notes: editNotes || null,
          quantity: parseInt(editQuantity),
        })
        .eq('delivery_id', selectedDelivery.delivery_id);
      if (singleError) throw new Error(`Failed to update delivery: ${singleError.message}`);
  
      // Update state for all deliveries
      const updatedDeliveries = deliveries.map(delivery => {
        if (invoiceEntirePackage && delivery.package_number === selectedDelivery.package_number) {
          return {
            ...delivery,
            invoice_number: editInvoiceNumber,
            quickbooks_invoice_id: editInvoiceNumber,
          };
        }
        if (delivery.delivery_id === selectedDelivery.delivery_id) {
          return {
            ...delivery,
            invoice_number: editInvoiceNumber,
            quickbooks_invoice_id: editInvoiceNumber,
            tracking_number: editTrackingNumber,
            ship_date: editShipDate,
            notes: editNotes,
            quantity: parseInt(editQuantity),
          };
        }
        return delivery;
      });

      await regeneratePackingSlip(selectedDelivery.package_number);

      setDeliveries(updatedDeliveries);
      setFilteredDeliveries(updatedDeliveries);

      await refreshDeliveries();
  
      closeModal();
      toast.success('Delivery updated and packing slip regenerated!');
    } catch (err) {
      console.error('Error updating delivery:', err);
      toast.error(`Failed to update delivery: ${err.message}`);
    }
    finally {
      setIsUpdating(false); // Stop loading
    }
  };

  const handleDeleteDelivery = async () => {
    const userConfirmed = window.confirm(
      'Are you sure you want to delete this delivery package? All items in this package will be deleted. This action cannot be undone.'
    );
    if (!userConfirmed) return;
  
    try {
      // Step 1: Fetch all deliveries with the same package_number
      const { data: packageDeliveries, error: fetchDeliveriesError } = await supabase
        .from('deliveries')
        .select('delivery_id, quantity, job_id, kanban_job_id')
        .eq('package_number', selectedDelivery.package_number);
      if (fetchDeliveriesError) throw new Error(`Failed to fetch package deliveries: ${fetchDeliveriesError.message}`);
  
      if (!packageDeliveries || packageDeliveries.length === 0) {
        throw new Error('No deliveries found for this package.');
      }
  
      // Step 2: Update shipped_quantity for each associated job/kanban_job
      for (const delivery of packageDeliveries) {
        const deliveryQty = delivery.quantity;
        const jobId = delivery.job_id;
        const kanbanJobId = delivery.kanban_job_id;
  
        // Update jobs table (if job_id exists)
        if (jobId) {
          const { data: jobData, error: fetchJobError } = await supabase
            .from('jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', jobId)
            .single();
          if (fetchJobError) throw new Error(`Failed to fetch job: ${fetchJobError.message}`);
  
          const newShippedQuantity = Math.max(0, jobData.shipped_quantity - deliveryQty);
          const newStatus =
            newShippedQuantity === 0
              ? 'ready_for_shipment'
              : newShippedQuantity === jobData.quantity_on_order
              ? 'closed'
              : 'partially_shipped';
  
          const { error: updateJobError } = await supabase
            .from('jobs')
            .update({
              shipped_quantity: newShippedQuantity,
              status: newStatus,
            })
            .eq('id', jobId);
          if (updateJobError) throw new Error(`Failed to update job shipped quantity: ${updateJobError.message}`);
        }
  
        // Update kanban_jobs table (if kanban_job_id exists)
        if (kanbanJobId) {
          const { data: kanbanJobData, error: fetchKanbanError } = await supabase
            .from('kanban_jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', kanbanJobId)
            .single();
          if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);
  
          const newKanbanShippedQuantity = Math.max(0, kanbanJobData.shipped_quantity - deliveryQty);
          const newStatus =
            newKanbanShippedQuantity === 0
              ? 'ready_for_shipment'
              : newKanbanShippedQuantity >= kanbanJobData.quantity_on_order
              ? 'closed'
              : 'partially_shipped';
  
          const { error: updateKanbanError } = await supabase
            .from('kanban_jobs')
            .update({
              shipped_quantity: newKanbanShippedQuantity,
              status: newStatus,
              updated_at: new Date().toISOString(),
            })
            .eq('id', kanbanJobId);
          if (updateKanbanError) throw new Error(`Failed to update Kanban job shipped quantity: ${updateKanbanError.message}`);
        }
      }
  
      // Step 3: Delete all delivery records with the same package_number
      const { error: deleteError } = await supabase
        .from('deliveries')
        .delete()
        .eq('package_number', selectedDelivery.package_number);
      if (deleteError) throw new Error(`Failed to delete package deliveries: ${deleteError.message}`);
  
      // Step 4: Delete the associated packing slip file from storage
      if (selectedDelivery.packing_slip_path) {
        const { error: storageError } = await supabase.storage
          .from('delivery-files')
          .remove([selectedDelivery.packing_slip_path]);
        if (storageError) console.warn(`Failed to delete packing slip: ${storageError.message}`);
      }
  
      // Step 5: Update the local state
      const updatedDeliveries = deliveries.filter(
        (delivery) => delivery.package_number !== selectedDelivery.package_number
      );
      setDeliveries(updatedDeliveries);
      setFilteredDeliveries(
        filteredDeliveries.filter(
          (delivery) => delivery.package_number !== selectedDelivery.package_number
        )
      );
  
      closeModal();
      toast.success('Delivery package deleted successfully!');
    } catch (err) {
      console.error('Error deleting delivery package:', err);
      toast.error(`Failed to delete delivery package: ${err.message}`);
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  
    const sortedData = [...groupedDeliveriesArray].sort((a, b) => {
      let aValue, bValue;
      if (field === 'unit_price') {
        aValue = a.unit_price || 0;
        bValue = b.unit_price || 0;
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (field === 'customer_nickname') {
        aValue = a.customer_nickname || '';
        bValue = b.customer_nickname || '';
      } else {
        aValue = a[field] || '';
        bValue = b[field] || '';
      }
      aValue = typeof aValue === 'string' ? aValue : String(aValue);
      bValue = typeof bValue === 'string' ? bValue : String(bValue);
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  
    setFilteredDeliveries(
      sortedData.flatMap(group => group.deliveries) // Flatten back to filteredDeliveries for consistency
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Package #',
      'PO Number',
      'Customer',
      'Total Quantity',
      'Unique Parts', // Add new column
      'Ship Date',
      'Invoice Status',
      'Tracking Number'
    ];
  
    const rows = groupedDeliveriesArray.map(group => [
      group.package_number,
      group.po_number,
      group.customer_nickname,
      group.total_quantity,
      group.unique_part_count, // Add new column value
      new Date(group.ship_date).toLocaleDateString(),
      group.invoice_number ? `Invoiced: ${group.invoice_number}` : 'Uninvoiced',
      group.tracking_number || '-'
    ]);
  
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');
  
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'deliveries_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const regeneratePackingSlip = async (packageNumber) => {
    try {
      // Fetch all deliveries for this package (renamed to avoid conflict)
      const { data: packageDeliveries, error: deliveriesError } = await supabase
        .from('deliveries')
        .select('*')
        .eq('package_number', packageNumber);
      
      if (deliveriesError) throw deliveriesError;
  
      // Fetch associated job data
      const jobIds = packageDeliveries.filter(d => d.job_id).map(d => d.job_id);
      const kanbanJobIds = packageDeliveries.filter(d => d.kanban_job_id).map(d => d.kanban_job_id);
      
      let jobsData = [];
      if (jobIds.length > 0) {
        const { data: jobs } = await supabase
          .from('jobs')
          .select('*, customers(*), parts(*)')
          .in('id', jobIds);
        jobsData = jobsData.concat(jobs || []);
      }
      
      if (kanbanJobIds.length > 0) {
        const { data: kanbanJobs } = await supabase
          .from('kanban_jobs')
          .select('*, customers(*)')
          .in('id', kanbanJobIds);
        jobsData = jobsData.concat(kanbanJobs || []);
      }
  
      // Generate new packing slip
      const pdfBlob = await generatePackingSlipPDF({
        deliveryData: packageDeliveries,
        jobsData: jobsData,
        poNumber: packageDeliveries[0].po_number,
        newDelivery: {
          ship_by: packageDeliveries[0].ship_by,
          tracking_number: packageDeliveries[0].tracking_number,
          notes: packageDeliveries[0].notes
        },
        isKanban: packageDeliveries.some(d => d.kanban_job_id),
        usePartNumberMaster: true
      });
  
      // Upload new PDF to storage
      const fileName = `packing_slip_${packageNumber}_${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('delivery-files')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: false
        });
  
      if (uploadError) throw uploadError;
  
      // Update all deliveries in the package with new packing slip path
      const { error: updateError } = await supabase
        .from('deliveries')
        .update({ packing_slip_path: uploadData.path })
        .eq('package_number', packageNumber);
  
      if (updateError) throw updateError;
  
      // Delete old packing slip if it exists
      const oldPath = packageDeliveries[0].packing_slip_path;
      if (oldPath) {
        await supabase.storage
          .from('delivery-files')
          .remove([oldPath]);
      }
  
      return uploadData.path;
    } catch (error) {
      console.error('Error regenerating packing slip:', error);
      throw error;
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  console.log('filteredDeliveries:', filteredDeliveries);

  if (!Array.isArray(filteredDeliveries)) {
    console.error('filteredDeliveries is not an array:', filteredDeliveries);
    return <div className="text-center text-red-500 py-10">Error: Unable to load deliveries. Please try again.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Deliveries</h1>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
          >
            Export to CSV
          </button>
        </div>

       {/* Filters */}
       <div className="bg-white p-4 rounded-lg mb-6 shadow-sm border border-gray-200">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 items-center">
            {/* Search Input for Package # and PO Number */}
            <div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by Package # or PO Number"
                className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
              />
            </div>

            {/* Customer Filter */}
            <div>
              <select
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.nickname || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* Show Uninvoiced Only Switch */}
            <div className="flex items-center">
              <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={showUninvoiced}
                  onChange={setShowUninvoiced}
                  className={`${
                    showUninvoiced ? 'bg-indigo-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      showUninvoiced ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
                <Switch.Label className="ml-3 text-sm font-medium text-gray-700">
                  Show Uninvoiced Only
                </Switch.Label>
              </Switch.Group>
            </div>

            {/* Total Uninvoiced Amount */}
            <div className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Total Uninvoiced Amount: $
                {filteredDeliveries
                  .filter(delivery => !delivery.invoice_number)
                  .reduce((sum, delivery) => {
                    const quantity = delivery.quantity || 0;
                    const unitPrice = delivery.unit_price || 0;
                    return sum + quantity * unitPrice;
                  }, 0)
                  .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Deliveries Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-scroll">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th
          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('package_number')}
        >
          Package {sortField === 'package_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th
          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('po_number')}
        >
          PO Number {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Quantity</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Unique Parts</th>
        <th
          className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => handleSort('ship_date')}
        >
          Shipped {sortField === 'ship_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
        </th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {groupedDeliveriesArray.map((packageGroup) => (
        <tr key={packageGroup.package_number} className="hover:bg-gray-50 transition duration-150">
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <button
              onClick={() => {
                setSelectedPackage(packageGroup);
                setIsLineItemModalOpen(true);
              }}
              className="text-indigo-600 hover:text-indigo-800 transition duration-150"
            >
              {packageGroup.package_number}
            </button>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{packageGroup.po_number}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{packageGroup.customer_nickname}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{packageGroup.total_quantity}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{packageGroup.unique_part_count}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(packageGroup.ship_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            {packageGroup.invoice_number ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Invoiced: {packageGroup.invoice_number}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Uninvoiced
              </span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm flex space-x-6">
            <button
              onClick={() => openEditModal(packageGroup.deliveries[0])}
              className="text-indigo-600 hover:text-indigo-800 transition duration-150 flex items-center space-x-1"
            >
              <PencilIcon className="w-5 h-5" />
              <span>Edit</span>
            </button>
            {packageGroup.packing_slip_path && (
              <button
                onClick={() => openPdfModal(packageGroup.packing_slip_path)}
                className="text-gray-600 hover:text-gray-800 transition duration-150 flex items-center font-bold"
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
                <span>View</span>
              </button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

        {/* Edit Delivery Modal */}
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Edit Delivery
                    </Dialog.Title>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Package #</label>
                        <input
                          type="text"
                          value={selectedDelivery?.package_number || ''}
                          disabled
                          className="block w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(e.target.value)}
                          placeholder="Enter quantity"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ship Date</label>
                        <input
                          type="date"
                          value={editShipDate}
                          onChange={(e) => setEditShipDate(e.target.value)}
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                        <input
                          type="text"
                          value={editTrackingNumber}
                          onChange={(e) => setEditTrackingNumber(e.target.value)}
                          placeholder="Enter tracking number"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          placeholder="Enter notes"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                          rows="3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                        <input
                          type="text"
                          value={editInvoiceNumber}
                          onChange={(e) => setEditInvoiceNumber(e.target.value)}
                          placeholder="Enter invoice number"
                          className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={invoiceEntirePackage}
                          onChange={(e) => setInvoiceEntirePackage(e.target.checked)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm font-medium text-gray-700">
                          Invoice Entire Package
                        </label>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={handleDeleteDelivery}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 shadow-sm"
                      >
                        <TrashIcon className="w-5 h-5 mr-2" />
                        Delete
                      </button>
                      <div className="flex space-x-3">
                        <button
                          onClick={closeModal}
                          className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
                        >
                          Cancel
                        </button>
                        <button
                        onClick={handleUpdateDelivery}
                        disabled={isUpdating}
                        className={`inline-flex items-center px-4 py-2 rounded-lg transition duration-200 shadow-sm ${
                          isUpdating 
                            ? 'bg-indigo-400 cursor-not-allowed' 
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        } text-white`}
                      >
                        {isUpdating ? (
                          <>
                            <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* PDF Viewer Modal */}
        <Transition appear show={isPdfModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closePdfModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-[90vw] h-[90vh] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
                      Packing Slip
                    </Dialog.Title>
                    <div className="mt-4 h-[calc(100%-6rem)] overflow-auto">
                      {pdfUrl ? (
                        <iframe
                          src={pdfUrl}
                          className="w-full h-full border border-gray-200 rounded-lg"
                          title="Packing Slip Preview"
                        />
                      ) : (
                        <p className="text-gray-500">Unable to load packing slip.</p>
                      )}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={closePdfModal}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Line Item Breakdown Modal */}
<Transition appear show={isLineItemModalOpen} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => setIsLineItemModalOpen(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
            <Dialog.Title as="h2" className="text-xl font-semibold text-gray-900 mb-4">
              Package #{selectedPackage?.package_number} - Line Items
            </Dialog.Title>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Part Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Unit Price</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ship Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracking Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedPackage?.deliveries.map((delivery) => (
                    <tr key={delivery.delivery_id} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.part_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {delivery.unit_price ? `$${delivery.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(delivery.ship_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{delivery.tracking_number || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={delivery.notes}>{delivery.notes || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => {
                            setIsLineItemModalOpen(false);
                            openEditModal(delivery);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 transition duration-150 flex items-center space-x-1"
                        >
                          <PencilIcon className="w-5 h-5" />
                          <span>Edit</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setIsLineItemModalOpen(false)}
                className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition duration-200 shadow-sm"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>
      </div>
    </div>
  );
}

export default Deliveries;